import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MerchantHeader from '../components/MerchantHeader';
import ErrorBoundary from '../components/ErrorBoundary';
import { LoadingPage } from '../components/LoadingState';
import { toast } from 'sonner';
import {
  Key, Eye, EyeOff, Trash2, CheckCircle2, XCircle,
  Bot, Sparkles, ChevronRight, RefreshCw, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter,
} from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MODELS = [
  { id: 'claude-sonnet-4-6', name: 'Claude Sonnet 4.6', desc: 'Fast, efficient — best for everyday chat', badge: 'Default' },
  { id: 'claude-opus-4-6', name: 'Claude Opus 4.6', desc: 'Most capable — deep analysis & complex tasks', badge: 'Premium' },
  { id: 'claude-haiku-4-5-20251001', name: 'Claude Haiku 4.5', desc: 'Fastest — quick lookups & simple queries', badge: 'Fast' },
];

export default function ChatSettingsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [removing, setRemoving] = useState(false);

  const getToken = () => sessionStorage.getItem('session_token');

  const fetchWithAuth = useCallback(async (endpoint, options = {}) => {
    const token = getToken();
    if (!token) { navigate('/login'); return null; }
    const res = await fetch(`${API_URL}${endpoint}`, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      ...options,
    });
    if (!res.ok) throw new Error(`Failed: ${endpoint}`);
    return res.json();
  }, [navigate]);

  const loadSettings = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/v1/chat/settings');
      setSettings(data?.settings || null);
    } catch (err) {
      console.error('Failed to load chat settings:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleSave = async (updates) => {
    setSaving(true);
    try {
      await fetchWithAuth('/api/v1/chat/settings', {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      toast.success('Chat settings updated');
      loadSettings();
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveKey = async () => {
    if (!apiKey.trim()) return;
    await handleSave({ anthropic_api_key: apiKey.trim() });
    setApiKey('');
  };

  const handleValidateKey = async () => {
    const keyToValidate = apiKey.trim() || null;
    if (!keyToValidate) {
      toast.error('Enter an API key to validate');
      return;
    }
    setValidating(true);
    setValidationResult(null);
    try {
      const result = await fetchWithAuth('/api/v1/chat/settings/validate', {
        method: 'POST',
        body: JSON.stringify({ api_key: keyToValidate }),
      });
      setValidationResult(result);
    } catch (err) {
      setValidationResult({ valid: false, error: 'Validation request failed' });
    } finally {
      setValidating(false);
    }
  };

  const handleRemoveKey = async () => {
    setRemoving(true);
    try {
      await fetchWithAuth('/api/v1/chat/settings/key', { method: 'DELETE' });
      toast.success('API key removed. Using platform key.');
      setShowRemoveDialog(false);
      loadSettings();
    } catch (err) {
      toast.error('Failed to remove API key');
    } finally {
      setRemoving(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-zinc-950">
      <MerchantHeader />
      <LoadingPage message="Loading chat settings..." />
    </div>
  );

  const keySource = settings?.key_source;

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-zinc-950 text-white">
        <MerchantHeader />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Bot className="w-6 h-6 text-cyan-400" /> AI Chat Settings
              </h1>
              <p className="text-zinc-400 text-sm mt-1">Configure Claude AI for conversational commerce</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard/settings')}>
              <ChevronRight className="w-4 h-4 mr-1 rotate-180" /> Settings
            </Button>
          </div>

          {/* API Key Configuration */}
          <Card className="bg-zinc-900/70 border-zinc-800 mb-6">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Key className="w-4 h-4 text-amber-400" /> Anthropic API Key
              </CardTitle>
              <CardDescription>
                Use your own Anthropic API key for Claude chat. If not set, the platform key is used.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Current status */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50">
                <div className={`w-2 h-2 rounded-full ${keySource === 'merchant' ? 'bg-emerald-400' : keySource === 'platform' ? 'bg-cyan-400' : 'bg-red-400'}`} />
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    {keySource === 'merchant' ? 'Using your API key' : keySource === 'platform' ? 'Using platform key' : 'No API key configured'}
                  </p>
                  {settings?.key_preview && (
                    <p className="text-xs text-zinc-500 font-mono mt-0.5">{settings.key_preview}</p>
                  )}
                </div>
                {keySource === 'merchant' && (
                  <Badge className="bg-emerald-900/50 text-emerald-400 border-emerald-800 text-xs">Custom Key</Badge>
                )}
                {keySource === 'platform' && (
                  <Badge className="bg-cyan-900/50 text-cyan-400 border-cyan-800 text-xs">Platform Key</Badge>
                )}
              </div>

              {/* Key input */}
              <div className="space-y-2">
                <label className="text-xs text-zinc-400">Enter Anthropic API Key</label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input
                      type={showKey ? 'text' : 'password'}
                      value={apiKey}
                      onChange={(e) => { setApiKey(e.target.value); setValidationResult(null); }}
                      placeholder="sk-ant-..."
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-sm font-mono text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-cyan-600"
                    />
                    <button
                      onClick={() => setShowKey(!showKey)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                    >
                      {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleValidateKey} disabled={validating || !apiKey.trim()}>
                    {validating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                    <span className="ml-1">{validating ? 'Checking...' : 'Validate'}</span>
                  </Button>
                </div>

                {/* Validation result */}
                {validationResult && (
                  <div className={`flex items-center gap-2 p-2 rounded text-xs ${validationResult.valid ? 'bg-emerald-900/30 text-emerald-400' : 'bg-red-900/30 text-red-400'}`}>
                    {validationResult.valid ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                    {validationResult.valid ? `Valid key — tested with ${validationResult.model_used}` : validationResult.error || 'Invalid key'}
                  </div>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex gap-2">
              <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" onClick={handleSaveKey} disabled={saving || !apiKey.trim()}>
                {saving ? 'Saving...' : 'Save API Key'}
              </Button>
              {settings?.has_merchant_key && (
                <Button variant="outline" size="sm" className="text-red-400 border-red-800 hover:bg-red-900/20" onClick={() => setShowRemoveDialog(true)}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove Key
                </Button>
              )}
            </CardFooter>
          </Card>

          {/* Model Selection */}
          <Card className="bg-zinc-900/70 border-zinc-800 mb-6">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-400" /> Default Model
              </CardTitle>
              <CardDescription>Choose the Claude model for standard chat messages</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {MODELS.map((model) => {
                  const isSelected = (settings?.default_model || 'claude-sonnet-4-6') === model.id;
                  return (
                    <button
                      key={model.id}
                      onClick={() => handleSave({ default_model: model.id })}
                      disabled={saving}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                        isSelected
                          ? 'border-cyan-600 bg-cyan-900/20'
                          : 'border-zinc-800 bg-zinc-800/30 hover:border-zinc-700'
                      }`}
                    >
                      <div className={`w-3 h-3 rounded-full border-2 ${isSelected ? 'border-cyan-400 bg-cyan-400' : 'border-zinc-600'}`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{model.name}</span>
                          <Badge variant="outline" className="text-[10px] px-1.5">{model.badge}</Badge>
                        </div>
                        <p className="text-xs text-zinc-500 mt-0.5">{model.desc}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Feature Toggles */}
          <Card className="bg-zinc-900/70 border-zinc-800 mb-6">
            <CardHeader>
              <CardTitle className="text-base">Feature Toggles</CardTitle>
              <CardDescription>Enable or disable chat features</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">AI Chat</p>
                  <p className="text-xs text-zinc-500">Enable the conversational commerce chat panel</p>
                </div>
                <Switch
                  checked={settings?.chat_enabled ?? true}
                  onCheckedChange={(checked) => handleSave({ chat_enabled: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    Deep Review Mode <Sparkles className="w-3.5 h-3.5 text-purple-400" />
                  </p>
                  <p className="text-xs text-zinc-500">Allow Opus 4.6 deep analysis with full business context</p>
                </div>
                <Switch
                  checked={settings?.deep_review_enabled ?? true}
                  onCheckedChange={(checked) => handleSave({ deep_review_enabled: checked })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Info */}
          {settings?.updated_at && (
            <p className="text-xs text-zinc-600 text-center">
              Last updated: {new Date(settings.updated_at).toLocaleString()}
            </p>
          )}
        </div>

        {/* Remove Key Confirmation */}
        <Dialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle>Remove API Key</DialogTitle>
              <DialogDescription>
                This will remove your custom Anthropic API key. Chat will fall back to the platform key.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRemoveDialog(false)}>Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700" onClick={handleRemoveKey} disabled={removing}>
                {removing ? 'Removing...' : 'Remove Key'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ErrorBoundary>
  );
}
